import { Radio } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'react-table';

import {
  BasicTable,
  ColumnAdditionalData,
  DateCell,
} from '@work4all/components';
import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';
import { useLock } from '@work4all/components/lib/hooks';

import { PurchasePrice } from '@work4all/models/lib/Classes/PurchasePrice.entity';

import { formatNumberAsCurrency } from '@work4all/utils';

export interface PurchasePriceTableProps {
  standardSupplierId: number;
  onStandardSupplierIdChange: (value: number) => void;
  onSelectedRowsChange: (rows: Row[]) => void;
  onRowDoubleClick: () => void;
  purchasePriceList: PurchasePrice[];
}

export function PurchasePriceTable(props: PurchasePriceTableProps) {
  const {
    standardSupplierId,
    onStandardSupplierIdChange,
    purchasePriceList,
    onSelectedRowsChange,
    onRowDoubleClick,
  } = props;

  const { t } = useTranslation();
  const { locked } = useLock();

  const columns = useMemo(() => {
    const columns: (Column<PurchasePrice> & ColumnAdditionalData)[] = [
      {
        id: 'isStandard',
        Header: t('MASK.ARTICLE.PURCHASE_PRICE.FIELDS.IS_STANDARD_PRICE'),
        accessor: (row) => row.supplier?.id === standardSupplierId,
        width: 100,
        Cell: (props) => {
          return (
            <Radio
              checked={props.value}
              onClick={() => {
                onStandardSupplierIdChange(
                  props.value ? 0 : props.row.original.supplier?.id
                );
              }}
              disabled={locked}
              sx={{
                display: 'block',
                width: 'max-content',
                margin: 'auto',
                padding: 0,
              }}
            />
          );
        },
      },
      {
        id: 'supplier',
        Header: t('COMMON.SUPPLIER'),
        accessor: (row) => {
          return row.supplier?.name;
        },
        width: 200,
      },
      {
        id: 'validFromDate',
        Header: t('MASK.VALID_FROM'),
        accessor: (row) => {
          return row.validFromDate;
        },
        width: 100,
        Cell: DateCell,
      },
      {
        id: 'listPrice',
        Header: t('MASK.LIST_PRICE'),
        alignment: 'right',
        accessor: (row) => {
          return formatNumberAsCurrency(row.listPrice);
        },
        width: 100,
      },
      {
        id: 'discount',
        Header: t('MASK.DISCOUNT'),
        alignment: 'right',
        accessor: (row) => {
          return `${formatNumberAsCurrency(row.discount)} %`;
        },
        width: 100,
      },
      {
        id: 'purchasePrice',
        Header: t('MASK.PURCHASE_PRICE_SHORT'),
        alignment: 'right',
        accessor: (row) => {
          return formatNumberAsCurrency(row.purchasePrice);
        },
        width: 100,
      },
      {
        id: 'currency',
        Header: t('COMMON.CURRENCY'),
        accessor: (row) => {
          return row.currency?.name;
        },
        width: 100,
      },
      {
        id: 'note',
        Header: t('MASK.NOTES_ROUTE'),
        accessor: (row) => {
          return row.note;
        },
        width: 150,
      },
      {
        id: 'unit',
        Header: (
          <Tooltip
            title={t('MASK.ARTICLE.PURCHASE_PRICE.FIELDS.PACKAGING_UNIT')}
          >
            <span>
              {t('MASK.ARTICLE.PURCHASE_PRICE.FIELDS.PACKAGING_UNIT_SHORT')}
            </span>
          </Tooltip>
        ),
        alignment: 'right',
        accessor: (row) => {
          return row.unit?.name;
        },
        width: 50,
      },
      {
        id: 'amountPerUnit',
        Header: t('MASK.ARTICLE.PURCHASE_PRICE.FIELDS.AMOUNT_PER_UNIT_SHORT'),
        alignment: 'right',
        accessor: (row) => {
          return row.amountPerUnit;
        },
        width: 100,
      },
    ];

    return columns;
  }, [locked, onStandardSupplierIdChange, standardSupplierId, t]);

  return (
    <BasicTable
      mode="client"
      columns={columns}
      data={purchasePriceList}
      isVirtual={false}
      fitContentHeight={true}
      resizableColumns={false}
      selectableMultiple={false}
      selectableRows
      onSelectedRowsChange={onSelectedRowsChange}
      onRowDoubleClick={onRowDoubleClick}
    />
  );
}
